import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Grid } from "@mui/material";
import { ArticleOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";

import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import PremiumDialog from "components/PremiumDialog/PremiumDialog";
import axios, { baseURL } from "utils/api";

const textareaStyle = {
  width: "100%",
  boxSizing: "border-box",
  minHeight: 64,
  resize: "vertical",
  border: "1px solid #d2d6da",
  borderRadius: "8px",
  padding: "10px 12px",
  fontSize: "14px",
  lineHeight: 1.5,
  fontFamily: "inherit",
  color: "#344767",
  outline: "none",
  backgroundColor: "#fff",
};

const emptyCompany = () => ({
  name: "",
  designation: "",
  duration: "",
  joiningSalary: "",
  lastSalary: "",
  reason: "",
});

const emptyPractical = () => ({
  excel: { result: "", remarks: "" },
  word: { result: "", remarks: "" },
  basic: { result: "", remarks: "" },
});

export const emptyScreeningForm = (candidate) => ({
  candidateName: candidate?.candidateName || "",
  role: candidate?.jobProfile || "",
  interviewer: "",
  date: "",
  phone: "",
  location: candidate?.locality || "",
  age: "",
  companies: [emptyCompany(), emptyCompany(), emptyCompany()],
  expectedSalary: "",
  notice: "",
  familyNotes: "",
  computerSkills: "",
  availability: "",
  practical: emptyPractical(),
  ratings: { english: "", employment: "", computer: "", jobFit: "" },
  evidence: { english: "", employment: "", computer: "", jobFit: "" },
  decision: "",
  finalRemarks: "",
  signature: "",
});

const wholeScore = (value) => {
  const score = String(value ?? "").trim();
  return ["1", "2", "3", "4", "5"].includes(score) ? score : "";
};

const fromOlderForm = (saved, base) => {
  const company = {
    ...emptyCompany(),
    designation: saved.designation || "",
    duration: saved.totalExperience || "",
    joiningSalary: saved.joiningSalary || "",
    lastSalary: saved.lastDrawnSalary || "",
    reason: saved.reasonForLeaving || "",
  };
  const matrix = Array.isArray(saved.matrix) ? saved.matrix : [];
  const ratingKeys = ["english", "employment", "computer", "jobFit"];
  const ratings = { ...base.ratings };
  const evidence = { ...base.evidence };
  ratingKeys.forEach((key, index) => {
    ratings[key] = wholeScore(matrix[index]?.score);
    evidence[key] = matrix[index]?.evidence || "";
  });
  return {
    ...base,
    candidateName: saved.candidateName || base.candidateName,
    role: saved.role || base.role,
    age: saved.age || "",
    companies: [company, emptyCompany(), emptyCompany()],
    expectedSalary: saved.expectedSalary || "",
    familyNotes: saved.familyOverview || "",
    computerSkills: [saved.softwareTools, saved.hardware].filter(Boolean).join(". "),
    ratings,
    evidence,
    finalRemarks: saved.summaryRemarks || "",
  };
};

export const screeningFromCandidate = (candidate) => {
  const base = emptyScreeningForm(candidate);
  const saved = candidate?.screeningAssessment;
  if (!saved || typeof saved !== "object") return base;
  if (!Array.isArray(saved.companies)) return fromOlderForm(saved, base);
  return {
    ...base,
    ...saved,
    candidateName: saved.candidateName || base.candidateName,
    role: saved.role || base.role,
    location: saved.location || base.location,
    companies: [0, 1, 2].map((index) => ({ ...emptyCompany(), ...(saved.companies[index] || {}) })),
    practical: {
      ...emptyPractical(),
      ...(saved.practical || {}),
      excel: { ...emptyPractical().excel, ...(saved.practical?.excel || {}) },
      word: { ...emptyPractical().word, ...(saved.practical?.word || {}) },
      basic: { ...emptyPractical().basic, ...(saved.practical?.basic || {}) },
    },
    ratings: { ...base.ratings, ...(saved.ratings || {}) },
    evidence: { ...base.evidence, ...(saved.evidence || {}) },
  };
};

function FieldLabel({ children }) {
  return (
    <ArgonTypography
      variant="caption"
      fontWeight="medium"
      color="text"
      mb={0.5}
      display="block"
      sx={{ textTransform: "none" }}
    >
      {children}
    </ArgonTypography>
  );
}

FieldLabel.propTypes = { children: PropTypes.node };

function SectionTitle({ children }) {
  return (
    <ArgonTypography
      variant="button"
      fontWeight="bold"
      mt={0.5}
      display="block"
      sx={{ textTransform: "none", color: "#1d4e9f" }}
    >
      {children}
    </ArgonTypography>
  );
}

SectionTitle.propTypes = { children: PropTypes.node };

function SelectField({ value, onChange, options, ariaLabel }) {
  return (
    <select aria-label={ariaLabel} value={value || ""} onChange={onChange} style={{ ...textareaStyle, minHeight: 36 }}>
      {options.map((option) => (
        <option key={option.value || "blank"} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

SelectField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  ariaLabel: PropTypes.string,
};

const SCORE_OPTIONS = [
  { value: "", label: "Score" },
  ...["1", "2", "3", "4", "5"].map((score) => ({ value: score, label: score })),
];

const RESULT_OPTIONS = [
  { value: "", label: "Not marked" },
  { value: "pass", label: "Pass" },
  { value: "needs", label: "Needs training" },
];

const PRACTICAL_FIELDS = [
  ["excel", "MS Excel"],
  ["word", "MS Word"],
  ["basic", "Basic computer"],
];

const RATING_FIELDS = [
  ["english", "English communication"],
  ["employment", "Employment history clarity"],
  ["computer", "Computer / MS Office skills"],
  ["jobFit", "Job fit & availability"],
];

function ScreeningForm({ candidate, onClose, onSaved }) {
  const [form, setForm] = useState(() => screeningFromCandidate(candidate));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(screeningFromCandidate(candidate));
  }, [candidate]);

  const setField = (field) => (event) => {
    const nextValue = event.target.value;
    setForm((current) => ({ ...current, [field]: nextValue }));
  };

  const setCompany = (index, field) => (event) => {
    const nextValue = event.target.value;
    setForm((current) => ({
      ...current,
      companies: current.companies.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: nextValue } : row
      ),
    }));
  };

  const setPractical = (key, field) => (event) => {
    const nextValue = event.target.value;
    setForm((current) => ({
      ...current,
      practical: {
        ...current.practical,
        [key]: { ...current.practical[key], [field]: nextValue },
      },
    }));
  };

  const setScore = (group, key) => (event) => {
    const nextValue = event.target.value;
    setForm((current) => ({
      ...current,
      [group]: { ...current[group], [key]: nextValue },
    }));
  };

  const handleSave = async () => {
    if (!form.candidateName.trim()) {
      toast.error("Candidate name is required");
      return;
    }
    setSaving(true);
    try {
      const response = await axios.put(
        `${baseURL}/in-house-ats/${candidate._id}/screening-assessment`,
        form
      );
      toast.success("Screening PDF generated");
      onSaved(response.data?.data);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not generate the screening PDF");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PremiumDialog
      open={Boolean(candidate)}
      onClose={saving ? undefined : onClose}
      title="Round 1 Screening Form"
      icon={ArticleOutlined}
      maxWidth="lg"
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ArgonTypography variant="caption" color="secondary" sx={{ textTransform: "none" }}>
            Answers print on the official Round 1 telephonic screening sheet.
          </ArgonTypography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FieldLabel>Candidate</FieldLabel>
          <ArgonInput value={form.candidateName} onChange={setField("candidateName")} size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FieldLabel>Role</FieldLabel>
          <ArgonInput value={form.role} onChange={setField("role")} placeholder="Admission Manager" size="small" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FieldLabel>Interviewer</FieldLabel>
          <ArgonInput value={form.interviewer} onChange={setField("interviewer")} size="small" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FieldLabel>Date</FieldLabel>
          <ArgonInput value={form.date} onChange={setField("date")} placeholder="04 Nov 2026" size="small" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FieldLabel>Phone</FieldLabel>
          <ArgonInput value={form.phone} onChange={setField("phone")} size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FieldLabel>Current location</FieldLabel>
          <ArgonInput value={form.location} onChange={setField("location")} size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FieldLabel>Age</FieldLabel>
          <ArgonInput value={form.age} onChange={setField("age")} size="small" />
        </Grid>

        <Grid item xs={12}>
          <SectionTitle>3. Previous employment — up to 3 companies</SectionTitle>
        </Grid>
        {form.companies.map((company, index) => (
          <Grid item xs={12} key={`company-${index}`}>
            <ArgonBox sx={{ border: "1px solid #e6edf5", borderRadius: "12px", p: 1.5 }}>
              <ArgonTypography variant="caption" fontWeight="bold" display="block" mb={1}>
                Company {index + 1}
              </ArgonTypography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <FieldLabel>Company name</FieldLabel>
                  <ArgonInput value={company.name} onChange={setCompany(index, "name")} size="small" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FieldLabel>Designation</FieldLabel>
                  <ArgonInput value={company.designation} onChange={setCompany(index, "designation")} size="small" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FieldLabel>Duration</FieldLabel>
                  <ArgonInput value={company.duration} onChange={setCompany(index, "duration")} size="small" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FieldLabel>Joining salary (₹)</FieldLabel>
                  <ArgonInput value={company.joiningSalary} onChange={setCompany(index, "joiningSalary")} size="small" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FieldLabel>Last drawn salary (₹)</FieldLabel>
                  <ArgonInput value={company.lastSalary} onChange={setCompany(index, "lastSalary")} size="small" />
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel>Reason for leaving</FieldLabel>
                  <ArgonInput value={company.reason} onChange={setCompany(index, "reason")} size="small" />
                </Grid>
              </Grid>
            </ArgonBox>
          </Grid>
        ))}
        <Grid item xs={12} sm={6}>
          <FieldLabel>Expected in-hand salary (₹)</FieldLabel>
          <ArgonInput value={form.expectedSalary} onChange={setField("expectedSalary")} size="small" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FieldLabel>Notice period / joining availability</FieldLabel>
          <ArgonInput value={form.notice} onChange={setField("notice")} size="small" />
        </Grid>

        <Grid item xs={12}>
          <SectionTitle>4. Family background</SectionTitle>
        </Grid>
        <Grid item xs={12}>
          <FieldLabel>Notes</FieldLabel>
          <textarea value={form.familyNotes} onChange={setField("familyNotes")} rows={2} style={textareaStyle} />
        </Grid>
        <Grid item xs={12}>
          <SectionTitle>5. Computer and MS Office skills</SectionTitle>
        </Grid>
        <Grid item xs={12}>
          <FieldLabel>Candidate-reported skills</FieldLabel>
          <textarea value={form.computerSkills} onChange={setField("computerSkills")} rows={2} style={textareaStyle} />
        </Grid>
        <Grid item xs={12}>
          <SectionTitle>6. Job fit and joining</SectionTitle>
        </Grid>
        <Grid item xs={12}>
          <FieldLabel>Availability / location remarks</FieldLabel>
          <textarea value={form.availability} onChange={setField("availability")} rows={2} style={textareaStyle} />
        </Grid>

        <Grid item xs={12}>
          <SectionTitle>Live practical test</SectionTitle>
        </Grid>
        {PRACTICAL_FIELDS.map(([key, label]) => (
          <Grid item xs={12} key={key}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <FieldLabel>{label}</FieldLabel>
                <SelectField
                  ariaLabel={`${label} result`}
                  value={form.practical[key].result}
                  onChange={setPractical(key, "result")}
                  options={RESULT_OPTIONS}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <FieldLabel>Remarks</FieldLabel>
                <ArgonInput value={form.practical[key].remarks} onChange={setPractical(key, "remarks")} size="small" />
              </Grid>
            </Grid>
          </Grid>
        ))}

        <Grid item xs={12}>
          <SectionTitle>HR evaluation (1–5)</SectionTitle>
        </Grid>
        {RATING_FIELDS.map(([key, label]) => (
          <Grid item xs={12} key={key}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={5}>
                <FieldLabel>{label}</FieldLabel>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FieldLabel>Rating</FieldLabel>
                <SelectField
                  ariaLabel={`${label} rating`}
                  value={form.ratings[key]}
                  onChange={setScore("ratings", key)}
                  options={SCORE_OPTIONS}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <FieldLabel>Evidence / remarks</FieldLabel>
                <ArgonInput value={form.evidence[key]} onChange={setScore("evidence", key)} size="small" />
              </Grid>
            </Grid>
          </Grid>
        ))}
        <Grid item xs={12} sm={6}>
          <FieldLabel>Decision</FieldLabel>
          <SelectField
            ariaLabel="Hiring decision"
            value={form.decision}
            onChange={setField("decision")}
            options={[
              { value: "", label: "Not marked" },
              { value: "shortlist", label: "Shortlist for Round 2" },
              { value: "hold", label: "Hold / Verify" },
              { value: "reject", label: "Reject" },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FieldLabel>Interviewer signature</FieldLabel>
          <ArgonInput value={form.signature} onChange={setField("signature")} size="small" />
        </Grid>
        <Grid item xs={12}>
          <FieldLabel>Final remarks</FieldLabel>
          <textarea value={form.finalRemarks} onChange={setField("finalRemarks")} rows={4} style={textareaStyle} />
        </Grid>
        <Grid item xs={12}>
          <ArgonBox display="flex" justifyContent="flex-end" gap={1.5}>
            <ArgonButton variant="outlined" color="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </ArgonButton>
            <ArgonButton variant="gradient" color="info" onClick={handleSave} disabled={saving}>
              {saving ? "Generating PDF..." : "Generate PDF"}
            </ArgonButton>
          </ArgonBox>
        </Grid>
      </Grid>
    </PremiumDialog>
  );
}

ScreeningForm.propTypes = {
  candidate: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default ScreeningForm;
